import { ICustomerHistoryItem } from '../common-interfaces/organization-customer-views';

const BOOKING_TYPES = new Set(['BOOKING', 'EVENT_BOOKING', 'RESOURCE_BOOKING']);

/** Money is only summed within one currency; a customer can spend SEK at one venue and EUR at another. */
export interface CurrencyTotal {
    /** Lower case currency code, or '' when the venue's currency is unknown. */
    currency: string;
    lifetimeValue: number;
    averageValue: number;
}

export interface CustomerStats {
    bookings: number;
    totals: CurrencyTotal[];
    lastVisit: Date | null;
}

/** Rounds away float noise such as 29467.300000000003. */
const toCents = (value: number): number => Math.round(value * 100) / 100;

/** Real numbers only: canceled bookings don't count, and visits in the future aren't "last visits". */
export function customerStats(items: readonly ICustomerHistoryItem[], now = new Date()): CustomerStats {
    const counted = items.filter((item) => item.status !== 'CANCELED');
    const bookings = counted.filter((item) => BOOKING_TYPES.has(item.type));
    const pastVisits = bookings.map((item) => new Date(item.date)).filter((date) => date.getTime() <= now.getTime());

    const byCurrency = new Map<string, { sum: number; count: number }>();
    for (const item of counted) {
        const value = item.price?.withVat;
        if (typeof value !== 'number' || !isFinite(value)) {
            continue;
        }
        const key = (item.currency ?? '').toLowerCase();
        const entry = byCurrency.get(key) ?? { sum: 0, count: 0 };
        entry.sum += value;
        entry.count += 1;
        byCurrency.set(key, entry);
    }

    const totals = [...byCurrency.entries()]
        .map(([currency, { sum, count }]) => ({ currency, lifetimeValue: toCents(sum), averageValue: toCents(sum / count) }))
        .sort((a, b) => b.lifetimeValue - a.lifetimeValue);

    return {
        bookings: bookings.length,
        totals,
        lastVisit: pastVisits.length ? new Date(Math.max(...pastVisits.map((d) => d.getTime()))) : null
    };
}
